/**
 * ScopeEffectCoeffectExplainer — the trio every CSK governed step lives in.
 *
 * A governed step is parameterized by three orthogonal pieces of data:
 *
 *   - **Scope**   — the structural region the step occupies (a context,
 *                   a fibre of a fibration, a stage in a graded type
 *                   system). Scopes form a category whose morphisms are
 *                   the legitimate reshapings.
 *   - **Effect**  — the change the step performs on the value (monadic
 *                   side: `T A`). Tracked by a monoid of grades when
 *                   composition matters.
 *   - **Coeffect** — what the step *requires from* its context to be
 *                    legal (comonadic side: `W A`). Tracked by a dual
 *                    monoid of demand-grades.
 *
 * Effects describe what a step *produces*; coeffects describe what it
 * *consumes*. Scope tells you where the step lives. The free orthogonal
 * baseline classifies generated cells as production or demand, so mixed
 * authority/effect bridges require explicit extension data.
 *
 * The card is a sibling explainer for the Foundation 𝔻₀ construction: the
 * free orthogonal graded double category whose horizontal edges are effects,
 * vertical edges are coeffects, and cells never implicitly couple the axes.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR, GRAY } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  ArrowDefs,
  ExplainerCard,
  ObjectNode,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.REQUIRE.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-scope-effect-coeffect",
  kind: "Categorical structure",
  title: "Scope · Effect · Coeffect",
  subtitle: "Where a step lives, what it produces, what it requires",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Free OGDC", href: "#explainer-free-orthogonal-graded-double-category", accent: ACCENT },
  { label: "Adjunction", href: "#explainer-adjunction" },
  { label: "Monad", href: "#explainer-monad" },
  { label: "Comonad", href: "#explainer-comonad", accent: ACCENT },
  { label: "Graded monad", href: "#explainer-graded-monad" },
  { label: "Bimonad", href: "#explainer-bimonad", accent: ACCENT },
  { label: "Beck-Chevalley", href: "#explainer-beck-chevalley" },
  { label: "REQUIRE (At)", href: "#explainer-comonad", accent: ACCENT },
];

// inline: local — a compact "factorization triangle" diagram that shows
// a governed step factoring into a coeffect (W) leg and an effect (T) leg,
// living within a scope morphism σ.
function FactorizationDiagram(): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 460 220"
      role="img"
      aria-label="Authority-effect factorization: a governed step factors as coeffect (W) then effect (T) within a scope morphism σ"
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={ACCENT} />
      <ArrowDefs id={`${markerId}-gray`} color={GRAY[600]} />
      {/* Three nodes: source, intermediate (after coeffect supplied), target (after effect produced). */}
      <ObjectNode cx={56} cy={120} label="A" accent={ACCENT} />
      <ObjectNode cx={232} cy={120} label="A" subscript="W" accent={ACCENT} />
      <ObjectNode cx={408} cy={120} label="B" subscript="T" accent={ACCENT} />
      {/* Coeffect leg: A → W(A) — comonadic, the step "is supplied with". */}
      <path
        d={`M 70 120 Q 144 60 218 120`}
        fill="none"
        stroke={ACCENT}
        strokeWidth={1.5}
        markerEnd={`url(#${markerId}-head)`}
      />
      <text
        x={144}
        y={60}
        textAnchor="middle"
        fontSize="13"
        fontFamily="'JetBrains Mono', 'Fira Code', monospace"
        fontWeight={600}
        fill={ACCENT}
      >
        coeffect · W
      </text>
      <text x={144} y={76} textAnchor="middle" fontSize="10" fill={GRAY[600]}>
        "supplies"
      </text>
      {/* Effect leg: W(A) → T(B) — monadic, the step "produces". */}
      <path
        d={`M 246 120 Q 320 60 394 120`}
        fill="none"
        stroke={ACCENT}
        strokeWidth={1.5}
        markerEnd={`url(#${markerId}-head)`}
      />
      <text
        x={320}
        y={60}
        textAnchor="middle"
        fontSize="13"
        fontFamily="'JetBrains Mono', 'Fira Code', monospace"
        fontWeight={600}
        fill={ACCENT}
      >
        effect · T
      </text>
      <text x={320} y={76} textAnchor="middle" fontSize="10" fill={GRAY[600]}>
        "produces"
      </text>
      {/* Composite arrow — the governed transition. */}
      <path
        d={`M 70 138 L 394 138`}
        fill="none"
        stroke={GRAY[600]}
        strokeWidth={1.25}
        strokeDasharray="4 3"
        markerEnd={`url(#${markerId}-gray-head)`}
      />
      <text x={232} y={160} textAnchor="middle" fontSize="11" fill={GRAY[600]}>
        governed step · σ ∈ Scope
      </text>
      <text
        x={232}
        y={196}
        textAnchor="middle"
        fontSize="11"
        fontFamily="'JetBrains Mono', 'Fira Code', monospace"
        fill={GRAY[700]}
      >
        f : A → B ⇝ f̂ : W(A) → T(B) in scope σ
      </text>
    </svg>
  );
}

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      A governed step in CSK is the data of three orthogonal things: a <strong>scope</strong> that
      says where it lives, an <strong>effect</strong> that says what it produces, and a{" "}
      <strong>coeffect</strong> that says what it requires. The Foundation free orthogonal baseline
      keeps those axes separate: generated cells are production cells or demand cells, and mixed
      authority/effect bridges must be supplied explicitly.
    </p>
  ),
  intuition: (
    <>
      <p>
        Think of a server endpoint that mutates a record. Three pieces of information govern it:
      </p>
      <ul>
        <li>
          <strong>Scope.</strong> Which tenant? Which service mesh? Which compliance domain? The
          scope is the structural region the call lives in.
        </li>
        <li>
          <strong>Effect.</strong> What does the call do to the world? Append to an audit log,
          mutate a row, emit a webhook, debit a budget. The effect is what comes <em>out</em>.
        </li>
        <li>
          <strong>Coeffect.</strong> What does the call demand of its context? A valid auth token, a
          non-empty budget, a precommitted intent, a fresh nonce. The coeffect is what must be
          supplied to make the call legal.
        </li>
      </ul>
      <p>
        Conventional type systems lump these together as "what does this function take and what does
        it return". CSK separates them because they answer different security questions and compose
        differently: effects accumulate <em>through</em> a sequence (monad-like), coeffects
        accumulate <em>around</em> a context (comonad-like), and scope tracks the structural region
        the whole story lives in (fibration-like).
      </p>
      <p>
        The point is that authority — capabilities, tokens, budgets, precommitments — is a coeffect,
        not an effect. It is what the call <em>consumes</em> from its context. In the Foundation
        <code>𝔻₀</code> construction, <code>Cell₀</code> has production and demand constructors
        only; a bridge that genuinely combines both axes is witnessed extension data, not ambient
        authority.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Fix a category <MathInline>{tex`\mathcal{C}`}</MathInline> of plain values and morphisms. A{" "}
        <strong>scope</strong> is an object <MathInline>{tex`\sigma`}</MathInline> of a base
        category <MathInline>{tex`\mathcal{S}`}</MathInline> whose morphisms are the legitimate
        scope reshapings (region inclusion, capability narrowing, tenant restriction).
      </p>
      <p>Attach two grade-indexed records to every scope:</p>
      <ul>
        <li>
          <strong>Effect.</strong> A graded monad{" "}
          <MathInline>{tex`T_{k} : \mathcal{C} \to \mathcal{C}`}</MathInline> on{" "}
          <MathInline>{tex`\mathcal{C}`}</MathInline>, indexed by an effect monoid{" "}
          <MathInline>{tex`(E, \cdot_{E}, e_{E})`}</MathInline>. Composition of effects multiplies
          grades by <MathInline>{tex`\cdot_{E}`}</MathInline>.
        </li>
        <li>
          <strong>Coeffect.</strong> A graded comonad{" "}
          <MathInline>{tex`W_{r} : \mathcal{C} \to \mathcal{C}`}</MathInline> on{" "}
          <MathInline>{tex`\mathcal{C}`}</MathInline>, indexed by a coeffect monoid{" "}
          <MathInline>{tex`(R, \cdot_{R}, e_{R})`}</MathInline>. Composition of coeffects multiplies
          grades by <MathInline>{tex`\cdot_{R}`}</MathInline>.
        </li>
      </ul>
      <p>
        A <strong>governed transition</strong> from <MathInline>{tex`A`}</MathInline> to{" "}
        <MathInline>{tex`B`}</MathInline> in scope <MathInline>{tex`\sigma`}</MathInline> at effect
        grade <MathInline>{tex`k`}</MathInline> and coeffect grade <MathInline>{tex`r`}</MathInline>{" "}
        is a morphism
      </p>
      <MathBlock ariaLabel="f from W_r(A) to T_k(B) decorated by scope σ">
        {tex`f : W_{r}(A) \;\xrightarrow{\sigma}\; T_{k}(B)`}
      </MathBlock>
      <p>
        with the scope decoration recording the structural region. The factorization is intentional:
        the coeffect <em>supplies</em> what the step demands, the effect <em>delivers</em> what the
        step produces, and the scope confines both.
      </p>
      <FactorizationDiagram />
      <p style={{ marginTop: "0.5rem" }}>
        <strong>Authority-effect factorization.</strong> At the Foundation cell level this is
        production-demand normal form:
      </p>
      <p style={{ textAlign: "center", fontFamily: "'JetBrains Mono', monospace" }}>
        <code>pd-classify : Cell₀ f k v₁ v₂ → GovernanceRole</code>
      </p>
      <p>
        <code>hor-cell</code> classifies as production, <code>ver-cell</code> classifies as demand,
        and <code>pd-unique</code> proves that any classifier agreeing on those constructors is the
        same classifier. Composability of bridged governed steps is then governed by the BC mate
        condition on the product fibration of <MathInline>{tex`(\mathcal{S}, E, R)`}</MathInline> —
        the seam/bridge framework specialized to the scope-effect-coeffect setting.
      </p>
      <p style={{ marginTop: "0.75rem" }}>
        The full structural target is the <strong>free orthogonal graded double category</strong>: a
        double category whose horizontal 1-cells carry effects, vertical 1-cells carry coeffects,
        and <code>Cell₀</code> has no mixed constructor. The Foundation proof path establishes the
        universal map from <code>𝔻₀</code> into any interpreted graded double category and proves
        the cell map is the unique catamorphism on <code>Cell₀</code>.
      </p>
    </>
  ),
  relevance: (
    <>
      <p>
        Scope · Effect · Coeffect is the bookkeeping system every CSK Structural Safety Mechanism
        lives in:
      </p>
      <ul>
        <li>
          <strong>PRECOMMIT (W).</strong> The effect grade tracks the precommitment payload; the
          coeffect grade tracks the precommitment <em>requirement</em>; the scope is the governance
          region. Composing two precommitted steps multiplies both grades.
        </li>
        <li>
          <strong>REQUIRE (At).</strong> A pure-coeffect SDP: the comonadic side dominates,
          coeffects compose, effects stay trivial. Authority is consumed; no new effect is produced.
        </li>
        <li>
          <strong>PERMIT (P).</strong> A slice-category instance: the scope is the authority over
          which the slice is taken; the coeffect is the capability grant; the effect is the
          authorized action.
        </li>
        <li>
          <strong>RES.BALANCE (R1 + R2).</strong> Linear-vs-compact-closed adjunction: effect-side
          consumption (<MathInline>{tex`- \otimes A`}</MathInline>) versus coeffect-side release (
          <MathInline>{tex`A^{*} \otimes -`}</MathInline>), with the scope tracking the resource
          budget.
        </li>
      </ul>
      <p>
        <strong>Concrete safety payoff.</strong> Production-demand normal form eliminates the
        ambiguity in "who has permission for this action". The coeffect side says{" "}
        <em>which authority must be consumed</em>; the effect side says{" "}
        <em>which effect is then allowed</em>. There is no "ambient authority": the type of the step
        requires the coeffect to be supplied. A mixed bridge is not discovered by the free baseline;
        it must be added as witnessed governance data.
      </p>
      <p>
        Read this card as the smallest taxonomy that lets CSK separate the <em>where</em> (scope),
        the <em>what</em> (effect), and the <em>what-from</em> (coeffect) of every governed step.
        Larger constructions — the free orthogonal graded double category, its initiality, and the
        production-demand classifier — are mechanized in the Foundation proof path linked below.
      </p>
    </>
  ),
  references: [
    {
      label: "Coeffect calculi (Petricek, Orchard, Mycroft 2014)",
      path: "https://www.cl.cam.ac.uk/~tp322/papers/coeffects.pdf",
      note: "External: the canonical reference for coeffects as graded comonadic contexts.",
    },
    {
      label: "Graded modal types (Orchard et al.)",
      path: "https://granule-project.github.io/",
      note: "External: the Granule language realizes a scope · effect · coeffect type theory.",
    },
    {
      label: "Comonad (MathLib, Agda)",
      path: "agda/MathLib/Monad/Comonad.agda",
      note: "Coeffect side: comonad axioms and co-Kleisli composition.",
    },
    {
      label: "GradedMonad (MathLibCore, Agda)",
      path: "agda/MathLibCore/CategoryTheory/GradedMonad.agda",
      note: "Effect side: the algebraic basis for graded effects.",
    },
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "G1 (Ambient Effect) is precisely 'effect without coeffect-graded authority'.",
    },
    {
      label: "Free orthogonal graded double category",
      path: "agda/Foundation/DifferentialSecurity/FreeOrthogonalGradedDoubleCategory.agda",
      note: "Foundation certificate for 𝔻₀, no mixed Cell₀ constructor, and production-demand normal form.",
    },
    {
      label: "Differential Security monograph",
      path: "theory/manuscripts/DifferentialSecurity/monograph/A6_Extended_Proofs.md",
      note: "Source horizon for the orthogonal baseline and proof-strength levels.",
    },
  ],
};

export function ScopeEffectCoeffectExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<FactorizationDiagram />}
      crossLinks={CROSS_LINKS}
    />
  );
}
